/**
 * Address privacy utilities for protecting property addresses from search engines
 * while maintaining user-friendly display for authenticated users
 */

export interface PropertyLocation {
  address: string;
  city: string;
  state: string;
  zip_code: string;
}

/**
 * Formats address display based on authentication status
 * @param location Property location data
 * @param isAuthenticated Whether the user is authenticated
 * @param showFullAddress Whether to show full address (for authenticated users)
 * @returns Formatted address string
 */
export const formatAddressForDisplay = (
  location: PropertyLocation,
  isAuthenticated: boolean = false,
  showFullAddress: boolean = false
): string => {
  // For unauthenticated users or when explicitly hiding full address
  if (!isAuthenticated || !showFullAddress) {
    return `${location.city}, ${location.state}`;
  }
  
  // For authenticated users, show full address
  return location.address;
};

/**
 * Removes the street number from an address for privacy
 * @param address Full address string (e.g., "17366 Cherrylawn St")
 * @returns Address without street number (e.g., "Cherrylawn St")
 */
export const obfuscateStreetNumber = (address: string): string => {
  // Remove leading numbers and common separators (spaces, dashes, etc.)
  return address.replace(/^\d+[-\s]+/, '').trim();
};

/**
 * Gets the secondary location info (city, state, zip)
 * This is always shown regardless of authentication status
 */
export const getSecondaryLocationInfo = (location: PropertyLocation): string => {
  return `${location.city}, ${location.state} ${location.zip_code}`;
};

/**
 * Determines if a page should be indexed by search engines
 * Property detail pages with full addresses should not be indexed
 * @param hasFullAddress Whether the page displays full addresses
 * @returns Whether the page should be indexed
 */
export const shouldIndexPage = (hasFullAddress: boolean): boolean => {
  return !hasFullAddress;
};

/**
 * Gets appropriate meta tags for SEO protection
 * @param hasFullAddress Whether the page contains full addresses
 * @returns Meta robot directives
 */
export const getMetaRobots = (hasFullAddress: boolean): string => {
  if (hasFullAddress) {
    return "noindex, nofollow, noarchive, nosnippet";
  }
  return "index, follow";
};

/**
 * Creates a privacy-safe property title for SEO
 * @param location Property location data  
 * @param isAuthenticated Whether user is authenticated
 * @returns SEO-safe title
 */
export const createSafePropertyTitle = (
  location: PropertyLocation,
  isAuthenticated: boolean = false
): string => {
  if (isAuthenticated) {
    return `Investment Property - ${location.address}, ${location.city}, ${location.state}`;
  }
  return `Investment Property in ${location.city}, ${location.state}`;
};